import React, { useState, useEffect, useMemo } from "react";
import { api } from "../../api/client";
import { usePersistentState } from "../../lib/usePersistentState";
import { CameraDevice } from "../../types";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { ColumnDef } from "@tanstack/react-table";
import {
  LayoutGrid,
  Table as TableIcon,
  VideoOff,
  RefreshCw,
  Wifi,
  Radio,
} from "lucide-react";

// Display status folds liveness + eye4 presence into what an operator reads:
//   Online — reporting now.
//   Quiet  — offline by the window but eye4 says reachable (no motion). Not down.
//   Dead   — eye4 says inactivated (needs reactivation).
//   No UID — never resolved an eye4 UID.
//   Offline— offline, not yet classified.
type Cat = "online" | "quiet" | "attention" | "offline";
function display(d: CameraDevice): { label: string; badge: string; cat: Cat } {
  if (d.status === "online")
    return { label: "Online", badge: "online", cat: "online" };
  if (d.eye4State === "active")
    return { label: "Quiet", badge: "quiet", cat: "quiet" };
  if (d.eye4State === "inactivated")
    return { label: "Dead", badge: "critical", cat: "attention" };
  if (!d.vuid) return { label: "No UID", badge: "warning", cat: "attention" };
  return { label: "Offline", badge: "offline", cat: "offline" };
}

const relTime = (iso?: string | null): string => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  const h = Math.floor(s / 3600);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

const ConnChip: React.FC<{ c?: string }> = ({ c }) => {
  if (c === "4g")
    return (
      <span
        style={{
          ...chip,
          background: "var(--info-bg)",
          border: "1px solid var(--info-border)",
          color: "var(--info-text)",
        }}
      >
        <Radio size={10} /> 4G
      </span>
    );
  if (c === "wifi")
    return (
      <span
        style={{
          ...chip,
          background: "var(--bg-surface)",
          border: "1px solid var(--border-dim)",
          color: "var(--text-muted)",
        }}
      >
        <Wifi size={10} /> WiFi
      </span>
    );
  return <span style={{ ...chip, color: "var(--text-dim)" }}>—</span>;
};

export const CamerasPage: React.FC = () => {
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [statusFilter, setStatusFilter] = usePersistentState<string>("cameras.statusFilter", "all");
  const [connFilter, setConnFilter] = usePersistentState<string>("cameras.connFilter", "all");
  const [siteFilter, setSiteFilter] = usePersistentState<string>("cameras.siteFilter", "all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      setDevices(await api.getDevices());
    } catch (err) {
      console.error("Failed to load devices", err);
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => {
    const c = { online: 0, quiet: 0, attention: 0, offline: 0 };
    devices.forEach((d) => {
      c[display(d).cat]++;
    });
    return c;
  }, [devices]);

  const filtered = useMemo(
    () =>
      devices.filter((d) => {
        if (statusFilter !== "all" && display(d).cat !== statusFilter)
          return false;
        if (connFilter !== "all") {
          const c = d.connectionType || "unknown";
          if (connFilter === "unknown" ? c !== "unknown" : c !== connFilter)
            return false;
        }
        if (siteFilter !== "all") {
          if (siteFilter === "__ungrouped__") {
            if (d.siteId) return false;
          } else if (d.siteId !== siteFilter) {
            return false;
          }
        }
        return true;
      }),
    [devices, statusFilter, connFilter, siteFilter],
  );

  // Distinct sites present in the loaded fleet, for the Site filter dropdown.
  const siteOptions = useMemo(() => {
    const m = new Map<string, string>();
    devices.forEach((d) => {
      if (d.siteId && d.siteName) m.set(d.siteId, d.siteName);
    });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [devices]);

  const columns = useMemo<ColumnDef<CameraDevice>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Camera",
        cell: (info) => {
          const d = info.row.original;
          return (
            <div>
              <div
                style={{
                  fontWeight: 600,
                  color: "var(--text-main)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {d.name} <ConnChip c={d.connectionType} />
              </div>
              <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
                {d.model || "—"}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "serial",
        header: "Serial / UID",
        cell: (info) => {
          const d = info.row.original;
          return (
            <div>
              <code style={{ color: "var(--primary-light)", fontWeight: 600 }}>
                {d.serial}
              </code>
              <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
                {d.vuid ? (
                  `UID ${d.vuid.slice(0, 10)}…`
                ) : (
                  <span style={{ color: "var(--warning-text)" }}>no UID</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "ownerName",
        header: "Owner",
        cell: (info) => {
          const d = info.row.original;
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{d.ownerName}</div>
              <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
                {d.ownerEmail}
              </div>
            </div>
          );
        },
      },
      {
        id: "site",
        header: "Site",
        accessorFn: (d) => d.siteName ?? "",
        cell: (info) => {
          const s = info.row.original.siteName;
          return s ? (
            <span style={{ fontSize: 12, color: "var(--text-main)" }}>{s}</span>
          ) : (
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>—</span>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (d) => display(d).label,
        cell: (info) => (
          <StatusBadge status={display(info.row.original).badge} />
        ),
      },
      {
        accessorKey: "lastHeartbeat",
        header: "Last seen",
        cell: (info) => (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {relTime(info.row.original.lastHeartbeat)}
          </span>
        ),
      },
    ],
    [],
  );

  const Pill: React.FC<{ v: string; label: string; n: number }> = ({
    v,
    label,
    n,
  }) => (
    <button
      onClick={() => setStatusFilter(v)}
      className={`btn btn-sm ${statusFilter === v ? "btn-primary" : "btn-secondary"}`}
    >
      {label}{" "}
      <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
        {n}
      </span>
    </button>
  );

  return (
    <div>
      <div className="firewall-banner" style={{ marginBottom: 16 }}>
        <VideoOff size={15} color="var(--primary-light)" />
        <div>
          <strong>Privacy line enforced:</strong> operator view shows fleet
          telemetry, UID resolution and presence — never video or recordings.
        </div>
      </div>

      {/* Status pills + connection filter + view toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Pill v="all" label="All" n={devices.length} />
          <Pill v="online" label="Online" n={counts.online} />
          <Pill v="quiet" label="Quiet" n={counts.quiet} />
          <Pill v="attention" label="Need attention" n={counts.attention} />
          <Pill v="offline" label="Offline" n={counts.offline} />
          <select
            className="select"
            value={connFilter}
            onChange={(e) => setConnFilter(e.target.value)}
            style={{ marginLeft: 6 }}
          >
            <option value="all">All connections</option>
            <option value="4g">4G only</option>
            <option value="wifi">WiFi only</option>
            <option value="unknown">Unknown</option>
          </select>
          {siteOptions.length > 0 && (
            <select
              className="select"
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              style={{ marginLeft: 6 }}
            >
              <option value="all">All sites</option>
              {siteOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
              <option value="__ungrouped__">Ungrouped</option>
            </select>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw size={14} /> <span>Refresh</span>
          </button>
          <button
            className={`btn btn-sm ${viewMode === "table" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setViewMode("table")}
          >
            <TableIcon size={14} /> <span>Table</span>
          </button>
          <button
            className={`btn btn-sm ${viewMode === "grid" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid size={14} /> <span>Cards</span>
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
        <DataTable
          storageKey="cameras"
          data={filtered}
          columns={columns}
          searchPlaceholder="Search camera name, serial, owner..."
          exportFilename="mwm_camera_fleet.csv"
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {filtered.map((dev) => {
            const s = display(dev);
            return (
              <div key={dev.id} className="card">
                <div className="card-header" style={{ marginBottom: 12 }}>
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "var(--text-main)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {dev.name} <ConnChip c={dev.connectionType} />
                    </div>
                    <code
                      style={{ fontSize: 11, color: "var(--primary-light)" }}
                    >
                      {dev.serial}
                    </code>
                  </div>
                  <StatusBadge status={s.badge} />
                </div>
                <div
                  style={{
                    fontSize: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <Row k="Customer" v={dev.ownerName} />
                  <Row k="UID" v={dev.vuid ? "resolved" : "no UID"} />
                  <Row k="Last seen" v={relTime(dev.lastHeartbeat)} />
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div
              style={{ padding: 24, color: "var(--text-muted)", fontSize: 13 }}
            >
              No cameras match.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <div style={{ display: "flex", justifyContent: "space-between" }}>
    <span style={{ color: "var(--text-muted)" }}>{k}:</span>
    <span style={{ fontWeight: 500 }}>{v}</span>
  </div>
);

const chip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  borderRadius: 999,
  padding: "0 7px",
  fontSize: 10.5,
  fontWeight: 600,
  lineHeight: "17px",
};
